import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { emailTemplateService, EmailTemplate, TemplateVariables } from '@/services/emailTemplateService';
import { Mail, Copy, Sparkles, Check, ChevronRight } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface EmailDraftModalProps {
  isOpen?: boolean;
  onClose?: () => void;
  onSelectTemplate?: (content: { subject: string; htmlBody: string; textBody: string }) => void;
  trigger?: React.ReactNode;
}

export default function EmailDraftModal({
  isOpen: externalIsOpen,
  onClose,
  onSelectTemplate,
  trigger,
}: EmailDraftModalProps) {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const { toast } = useToast();
  
  const isOpen = externalIsOpen !== undefined ? externalIsOpen : internalIsOpen;
  const setIsOpen = (value: boolean) => {
    if (externalIsOpen === undefined) {
      setInternalIsOpen(value);
    }
    if (value === false && onClose) {
      onClose();
    }
  };

  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
  const [variables, setVariables] = useState<TemplateVariables>({});
  const [preview, setPreview] = useState<{
    subject: string;
    htmlBody: string;
    textBody: string;
  } | null>(null);
  const [activeTab, setActiveTab] = useState<'select' | 'fill' | 'preview'>('select');
  
  const templates = emailTemplateService.getAllTemplates();
  const categories = emailTemplateService.getCategories();

  const handleTemplateSelect = (template: EmailTemplate) => {
    setSelectedTemplate(template);
    setVariables({});
    setPreview(null);
    setActiveTab('fill');
  };

  const handleVariableChange = (key: string, value: string) => {
    setVariables(prev => ({ ...prev, [key]: value }));
  };

  const handleGeneratePreview = () => {
    if (!selectedTemplate) return;

    const filled = emailTemplateService.fillTemplate(selectedTemplate, variables);
    setPreview(filled);
    setActiveTab('preview');
  };

  const handleCopyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: 'Copied!',
      description: `${label} copied to clipboard!`,
    });
  };

  const handleUseTemplate = () => {
    if (!preview) return;
    
    if (onSelectTemplate) {
      onSelectTemplate(preview);
    }
    
    setIsOpen(false);
    toast({
      title: 'Success!',
      description: 'Template loaded! You can now send or edit it.',
    });
  };

  const templateVars = selectedTemplate ? emailTemplateService.getTemplateVariables(selectedTemplate.id) : [];
  const allVariablesFilled = templateVars.every(v => variables[v]);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" className="gap-2">
            <Mail className="h-4 w-4" />
            Email Templates
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-primary" />
            Email Draft Templates
          </DialogTitle>
          <DialogDescription>
            Choose a template, fill in the details, and instantly get a ready-to-send email.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={(value: any) => setActiveTab(value)} className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="select">Select Template</TabsTrigger>
            <TabsTrigger value="fill" disabled={!selectedTemplate}>
              Customize
            </TabsTrigger>
            <TabsTrigger value="preview" disabled={!preview}>
              Preview
            </TabsTrigger>
          </TabsList>

          {/* SELECT TEMPLATE TAB */}
          <TabsContent value="select" className="space-y-4">
            {categories.map(category => (
              <div key={category} className="space-y-3">
                <h3 className="text-sm font-semibold text-muted-foreground">{category}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {emailTemplateService.getTemplatesByCategory(category).map(template => (
                    <Card
                      key={template.id}
                      className="cursor-pointer hover:bg-accent/50 transition-colors"
                      onClick={() => handleTemplateSelect(template)}
                    >
                      <CardHeader className="pb-3">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1">
                            <CardTitle className="text-base">{template.name}</CardTitle>
                            <CardDescription className="text-xs mt-1">{template.description}</CardDescription>
                          </div>
                          <ChevronRight className="h-4 w-4 text-muted-foreground" />
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="flex items-center justify-between">
                          <Badge variant="secondary" className="text-xs">
                            {template.variables?.length || 0} fields
                          </Badge>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleTemplateSelect(template);
                            }}
                          >
                            Select
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </TabsContent>

          {/* CUSTOMIZE TAB */}
          <TabsContent value="fill" className="space-y-4">
            {selectedTemplate && (
              <>
                <div className="bg-muted p-4 rounded-lg border">
                  <p className="text-sm font-semibold mb-2">Selected: {selectedTemplate.name}</p>
                  <p className="text-xs text-muted-foreground">{selectedTemplate.description}</p>
                </div>

                <div className="space-y-4">
                  <h4 className="font-semibold text-sm">Fill in the details:</h4>
                  {templateVars.map(variable => (
                    <div key={variable} className="space-y-2">
                      <Label htmlFor={variable} className="capitalize">
                        {variable.replace(/_/g, ' ')}
                      </Label>
                      {variable.length > 50 || variable.includes('additional') || variable.includes('message') || variable.includes('details') ? (
                        <Textarea
                          id={variable}
                          placeholder={`Enter ${variable.replace(/_/g, ' ').toLowerCase()}...`}
                          value={variables[variable] || ''}
                          onChange={(e) => handleVariableChange(variable, e.target.value)}
                          className="min-h-[100px]"
                        />
                      ) : (
                        <Input
                          id={variable}
                          placeholder={`Enter ${variable.replace(/_/g, ' ').toLowerCase()}...`}
                          value={variables[variable] || ''}
                          onChange={(e) => handleVariableChange(variable, e.target.value)}
                        />
                      )}
                    </div>
                  ))}
                </div>

                <div className="flex gap-2 pt-4">
                  <Button
                    onClick={() => handleGeneratePreview()}
                    disabled={!allVariablesFilled}
                    className="flex-1 gap-2"
                  >
                    <Sparkles className="h-4 w-4" />
                    Generate Preview
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setSelectedTemplate(null);
                      setVariables({});
                      setPreview(null);
                      setActiveTab('select');
                    }}
                  >
                    Change Template
                  </Button>
                </div>
              </>
            )}
          </TabsContent>

          {/* PREVIEW TAB */}
          <TabsContent value="preview" className="space-y-4">
            {preview && (
              <>
                <div className="space-y-4">
                  {/* Subject Preview */}
                  <div className="space-y-2">
                    <h4 className="font-semibold text-sm flex items-center gap-2">
                      Subject
                    </h4>
                    <div className="bg-muted p-3 rounded-lg border text-sm">
                      {preview.subject || '(No subject)'}
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="gap-2"
                      onClick={() => handleCopyToClipboard(preview.subject, 'Subject')}
                    >
                      <Copy className="h-3 w-3" />
                      Copy Subject
                    </Button>
                  </div>

                  {/* HTML Body Preview */}
                  <div className="space-y-2">
                    <h4 className="font-semibold text-sm">Email Preview (HTML)</h4>
                    <ScrollArea className="h-64 border rounded-lg p-4 bg-card">
                      <div
                        className="prose prose-sm dark:prose-invert max-w-none"
                        dangerouslySetInnerHTML={{
                          __html: preview.htmlBody
                        }}
                      />
                    </ScrollArea>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="gap-2"
                      onClick={() => handleCopyToClipboard(preview.htmlBody, 'HTML Body')}
                    >
                      <Copy className="h-3 w-3" />
                      Copy HTML
                    </Button>
                  </div>

                  {/* Text Body Preview */}
                  <div className="space-y-2">
                    <h4 className="font-semibold text-sm">Text Version</h4>
                    <ScrollArea className="h-40 border rounded-lg p-4 bg-muted font-mono text-sm">
                      <pre className="whitespace-pre-wrap text-xs">{preview.textBody}</pre>
                    </ScrollArea>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="gap-2"
                      onClick={() => handleCopyToClipboard(preview.textBody, 'Text Body')}
                    >
                      <Copy className="h-3 w-3" />
                      Copy Text
                    </Button>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2 pt-4 border-t">
                  <Button onClick={handleUseTemplate} className="flex-1 gap-2 bg-primary hover:bg-primary/90">
                    <Check className="h-4 w-4" />
                    Use This Template
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setVariables({});
                      setPreview(null);
                      setActiveTab('fill');
                    }}
                  >
                    Edit
                  </Button>
                </div>
              </>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
